<template>
    <Float
        composable
        portal
        :show="calendarVisibility"
        :offset="6"
        placement="bottom-start"
        enter="transition duration-200 ease-out"
        enter-from="opacity-0 -translate-y-1"
        enter-to="opacity-100 translate-y-0"
        leave="transition duration-150 ease-in"
        leave-from="opacity-100 translate-y-0"
        leave-to="opacity-0 -translate-y-1"
    >
        <div
            ref="calendarWrapper"
            v-on-click-outside="hideCalendarFromClickOutside"
            class="forms__field_input--date"
            :class="[$attrs.class, { 'forms__field_input--date--has-time': props.time }]"
        >
            <InputGroup class="forms__field_input--date__input-group">
                <FloatReference>
                    <div class="forms__field_input--date__inner">
                        <AdminText
                            v-if="props.date"
                            v-model="displayDate"
                            :dusk="props.dusk"
                            type="text"
                            :size="props.size"
                            :placeholder="props.placeholder"
                            class="forms__field_input--date__date"
                            :class="[$attrs.class && $attrs.class.indexOf('border') !== -1 ? 'border-0' : '']"
                            :required="props.required"
                            :disabled="props.disabled"
                            @focusin="showCalendar"
                            @focusout="delayedHideCalendar"
                        />
                        <TimeInput
                            v-if="props.time"
                            v-model="displayTime"
                            :size="props.size"
                            :nullable-time="props.nullableTime"
                            :disabled="props.disabled"
                            class="forms__field_input--date__time"
                        />
                    </div>
                </FloatReference>
                <button
                    v-if="props.calendar && props.date"
                    type="button"
                    class="forms__field_input--date__trigger"
                    :disabled="props.disabled"
                    @click="focusInput"
                >
                    <Icon icon="hero/outline/calendar" class="w-4 h-4" />
                </button>
            </InputGroup>

            <FloatContent style="z-index: 99999">
                <ui-popover
                    v-show="calendarVisibility"
                    ref="calendarContent"
                    class="forms__field_input--date__calendar !w-[360px]"
                >
                    <Calendar v-model="displayCalendar" @picked="hideCalendar"></Calendar>
                </ui-popover>
            </FloatContent>
        </div>
    </Float>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { DateTime } from 'luxon';
import { vOnClickOutside } from '@vueuse/components';
import { ComponentSize } from '@src/Common/DataGrid/types';
import Calendar from '@src/Fields/Pages/Datetime/Calendar.vue';
import InputGroup from '@src/Common/Components/InputGroup.vue';
import { Float, FloatContent, FloatReference } from '@headlessui-float/vue';
import { DateHelper } from '@src/Common/Helpers/dates';

defineOptions({
    inheritAttrs: false,
});

interface Props {
    modelValue?: string | null;
    dusk?: string | null;
    size?: ComponentSize;
    time?: boolean;
    date?: boolean;
    placeholder?: string;
    calendar?: boolean;
    required?: boolean;
    nullable?: boolean;
    nullableTime?: boolean;
    disabled?: boolean;
}

const calendarWrapper = ref(null);
const calendarContent = ref(null);

const emit = defineEmits<{
    (event: 'update:modelValue', content: string | null): void;
}>();

const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    dusk: null,
    size: 'base',
    time: false,
    date: true,
    placeholder: '',
    calendar: true,
    required: false,
    nullable: false,
    nullableTime: false,
    disabled: false,
});

/**
 * Calendar element controls
 */
const calendarVisibility = ref(false);

const showCalendar = () => {
    if (props.disabled) {
        return;
    }

    calendarVisibility.value = true;
};

const hideCalendar = () => {
    calendarVisibility.value = false;
};

const hideCalendarFromClickOutside = (event: PointerEvent) => {
    // Ignore clicks inside the portaled calendar content
    if (calendarContent.value?.$el?.contains(event.target as Node)) {
        return;
    }
    hideCalendar();
};

const delayedHideCalendar = ($event) => {
    if (calendarWrapper.value && calendarWrapper.value.contains($event.target)) {
        return;
    }
    setTimeout(() => {
        hideCalendar();
    }, 200);
};

/**
 * Model values
 */
const form = ref({
    date: '',
    time: '',
});

const localDisplayDate = ref('');

onMounted(() => {
    const dateHelper = new DateHelper(props.modelValue);
    if (props.nullable && !props.modelValue) {
        form.value.date = '';
        form.value.time = '';
        localDisplayDate.value = '';
    } else {
        form.value.date = dateHelper.toISODate() || new DateHelper(DateTime.now()).toISODate();
        // For nullableTime, start with empty time if no modelValue exists
        if (props.nullableTime && !props.modelValue) {
            form.value.time = '';
        } else {
            form.value.time = dateHelper.toISOTime() || new DateHelper(DateTime.now()).toISOTime();
        }
        localDisplayDate.value = new DateHelper(props.modelValue).toHumanDate();
        // Only emit default value if not nullable and not nullableTime
        if (!props.nullable && !props.nullableTime && !props.modelValue) {
            emit('update:modelValue', `${form.value.date}T${form.value.time}`);
        }
    }
});

/**
 * Display values
 */
const displayDate = computed({
    get(): string {
        return localDisplayDate.value;
    },
    set(value: string) {
        localDisplayDate.value = value;
        if (props.nullable && !value) {
            form.value.date = '';
            emit('update:modelValue', null);
            return;
        }

        try {
            const parsedDateHelper = new DateHelper().fromHumanDate(value);
            const isoDate = parsedDateHelper.toISODate();
            if (isoDate) {
                form.value.date = isoDate;
                updateModelValue();
            }
        } catch {
            return;
        }
    },
});

const displayTime = computed({
    get(): string {
        if (props.nullable && !props.modelValue) {
            return '';
        }
        // For nullableTime, show empty if no modelValue or if time is empty
        if (props.nullableTime && (!props.modelValue || !form.value.time)) {
            return '';
        }
        return new DateHelper(props.modelValue).toHumanTime();
    },
    set(value: string) {
        if ((props.nullable || props.nullableTime) && !value) {
            form.value.time = '';
        } else {
            form.value.time = new DateHelper().fromHumanTime(value, form.value.date).toISOTime();
        }
        updateModelValue();
    },
});

const displayCalendar = computed({
    get(): string {
        return new DateHelper(props.modelValue).toISO();
    },
    set(value: DateTime) {
        form.value.date = value.toISODate();

        localDisplayDate.value = new DateHelper(form.value.date).toHumanDate();
        updateModelValue();
    },
});

function updateModelValue() {
    switch (true) {
        case props.date && props.time:
            if (form.value.date && !form.value.time) {
                // For nullableTime, if time is empty, save date with 00:00 time
                if (props.nullableTime) {
                    const localOffset = DateTime.local().toFormat('ZZ');
                    emit('update:modelValue', `${form.value.date}T00:00:00.000${localOffset}`);
                    return;
                }
                const localOffset = DateTime.local().toFormat('ZZ');
                emit('update:modelValue', `${form.value.date}T00:00:00.000${localOffset}`);
                return;
            }
            try {
                new DateHelper(`${form.value.date}T${form.value.time}`).toISO();
            } catch {
                return;
            }
            emit('update:modelValue', `${form.value.date}T${form.value.time}`);
            break;

        case props.date:
            if (props.nullable && !form.value.date) {
                emit('update:modelValue', null);
                return;
            }
            try {
                new DateHelper(form.value.date).toISO();
            } catch {
                return;
            }
            emit('update:modelValue', form.value.date);
            break;

        case props.time:
            emit('update:modelValue', form.value.time);
            break;
    }
}

/**
 * Focus date input to trigger show calendar
 */
const focusInput = () => {
    const input = calendarWrapper.value.querySelector('input');
    if (input) {
        input.focus();
    }
};

watch(
    () => props.modelValue,
    (newVal) => {
        if (newVal) {
            const dateHelper = new DateHelper(newVal);
            localDisplayDate.value = dateHelper.toHumanDate();
            form.value.date = dateHelper.toISODate();
            form.value.time = dateHelper.toISOTime();
        }
    },
);
</script>
